import { useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { btnStyle } from '../styles';

/**
 * One photograph in wizard step 5: a camera button, a gallery button and a
 * preview, or the note that this one is already on the record.
 *
 * Two inputs rather than one, because `capture` on a single input takes the
 * choice away: on a phone it opens the camera and offers no way to reach a
 * photograph already taken, which is what somebody re-doing a record needs.
 */
// ---------- PhotoSlot ----------
export default function PhotoSlot({ label, hint, required, file, alreadyOnFile, alreadyOnFileLabel, onChange }) {
  const { t } = useTranslation('collections');
  const cameraRef = useRef(null);
  const galleryRef = useRef(null);
  const previewUrl = file ? URL.createObjectURL(file) : null;
  const uid = label.replace(/\s+/g, '-');
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '9px 0', borderBottom: '1px solid var(--c-bg-muted)' }}>
      <div style={{ flex: 1, fontSize: 13, fontWeight: 500 }}>
        {label}
        {required && !file && !alreadyOnFile && <span style={{ color: 'var(--c-danger-2-fg)', marginInlineStart: 3 }}>*</span>}
        {!required && <span style={{ color: 'var(--c-text-muted-fg)', fontWeight: 400, marginInlineStart: 6, fontSize: 12 }}>{t('step5.optionalTag')}</span>}
        {file && <span style={{ color: 'var(--c-success-fg)', marginInlineStart: 6, fontSize: 12 }}>✓</span>}
        {!file && alreadyOnFile && <span style={{ color: 'var(--c-success-fg)', marginInlineStart: 6, fontSize: 11 }}>✓ {alreadyOnFileLabel}</span>}
        {hint && <div style={{ fontSize: 11, fontWeight: 400, color: 'var(--c-text-muted-fg)', marginTop: 2 }}>{hint}</div>}
      </div>
      {file ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <img src={previewUrl} alt="" style={{ width: 44, height: 44, objectFit: 'cover', borderRadius: 4, border: '1px solid var(--c-border)' }} />
          <span style={{ fontSize: 11, color: 'var(--c-text-muted-fg)', maxWidth: 100, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{file.name}</span>
          <button type="button" onClick={() => onChange(null)} title={t('tip.photoRemove')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--c-text-faint-fg)', fontSize: 18, lineHeight: 1, padding: '0 2px' }}>×</button>
        </div>
      ) : (
        <div style={{ display: 'flex', gap: 6 }}>
          <input ref={cameraRef} type="file" accept="image/*" capture="environment" style={{ display: 'none' }}
            onChange={e => { if (e.target.files[0]) onChange(e.target.files[0]); e.target.value = ''; }} />
          <button type="button" onClick={() => cameraRef.current?.click()} title={t('tip.photoCamera')} style={{ ...btnStyle('var(--c-info)'), padding: '5px 10px', fontSize: 12 }}>📷</button>
          <input ref={galleryRef} type="file" accept="image/*" id={`gal-${uid}`} style={{ display: 'none' }}
            onChange={e => { if (e.target.files[0]) onChange(e.target.files[0]); e.target.value = ''; }} />
          <label htmlFor={`gal-${uid}`} title={t('tip.photoGallery')} style={{ ...btnStyle('var(--c-text-muted)'), padding: '5px 10px', fontSize: 12, cursor: 'pointer', display: 'inline-block', lineHeight: '1.5' }}>🖼</label>
        </div>
      )}
    </div>
  );
}

// ---------- SubSection ----------
